import { useDialog } from 'primevue/usedialog'
import { PromotionErrorType, SECTION_NAME_PROMOTION } from '~/lib/models/promotions'
import { PromotionType, PromotionKind } from '~/lib/services/promos/promos.dto'

export const usePromotionErrorHandling = () => {
  const appStore = useAppStore()
  const { appliedPromotions, applyPromotion } = usePromotions()
  const { userSegmentation } = useProductPromos()
  const { t } = useLocale()
  const dialog = useDialog()

  const appliedPromotion = computed(() => appliedPromotions.value?.[0] || undefined)

  const errorCumulativePromotion = computed(() => appStore.getErrorCode)

  const isComulativePromotionError = (errorCode: string) => {
    return ([PromotionErrorType.status835, PromotionErrorType.status836] as string[]).includes(
      errorCode
    )
  }

  const getErrorForSection = (section: string) => {
    if (section === SECTION_NAME_PROMOTION.CART) {
      return PromotionErrorType.status836 // Error occurred in message
    } else if (section === SECTION_NAME_PROMOTION.PRODUCT_DETAIL) {
      return PromotionErrorType.status835 // Error occurred in product detail
    }
    return PromotionErrorType.status835
  }

  const showErrorPromotion = async (section: string) => {
    // Error type for section currrent
    const errorSectionType = getErrorForSection(section)
    // Show error modal
    const { result } = await showModalErrorPromotion(errorSectionType)
    // Result of the action by the user in the modal, Accept, Cancel or Close
    // If the user accepted the action (result === true or undefined)
    // If the user canceled the action (result === false)
    const userDecision = result === true || result === undefined
    // Get promotions and discounts
    const { isPromoAutoapply, isCoupon, hasProductRegularInCart, employeeDiscount } =
      getPromoState()
    // Products in the cart with auto applicable promotions
    if (isPromoAutoapply) {
      // Products in the cart with coupon is active
      if (isCoupon) {
        // Adds a product with an auto-applied promotion from the product detail and has a coupon promotion applied
        if (hasProductRegularInCart && section === SECTION_NAME_PROMOTION.PRODUCT_DETAIL) {
          userDecision ? disableProductPromotionsForCart() : removeAppliedPromotionByCode()
        } else if (section === SECTION_NAME_PROMOTION.CART) {
          userDecision ? removeAppliedPromotionByCode() : disableProductPromotionsForCart()
        } else {
          userDecision ? disableProductPromotionsForCart() : removeAppliedPromotionByCode()
        }
      }
      // Products in the cart with employee discount is active
      if (employeeDiscount) {
        userDecision
          ? appStore.removePromoById(employeeDiscount.promotionId)
          : disableProductPromotionsForCart()
      }
    }
  }

  const disableProductPromotionsForCart = () => {
    // Apply new and disable actual cart promotions
    appStore.disableAllProductPromotions()
    // Update discounts applied by employee or coupon
    appStore.fetchCart()
  }

  const removeAppliedPromotionByCode = () => {
    // Discard promotion by code
    if (appliedPromotion.value?.promotionId)
      applyPromotion({
        promo: undefined,
        promotionIdToRemove: appliedPromotion.value.promotionId,
      })
  }

  const getPromoState = () => {
    // Exist promo auto apply
    const isPromoAutoapply = !!appStore.cart.products.find(
      product =>
        product.promotions?.some(
          promotion => promotion.type !== PromotionType.SHIPPING_PERCENTAGE
        ) && validPromoForBubble(product.promotions, userSegmentation.value)
    )
    // Promo is type coupon
    const isCoupon = appliedPromotion.value?.kind === PromotionKind.COUPON
    // Check if employee discount is applied
    const employeeDiscount = appStore.appliedPromotions?.find(
      promo => promo.code === undefined && promo.enabled
    )
    // Product regular in cart
    const hasProductRegularInCart = appStore.cart.products.some(
      product => product.promotions?.length === 0
    )
    return { isPromoAutoapply, isCoupon, employeeDiscount, hasProductRegularInCart }
  }

  const showModalErrorPromotion = async (errorCode: string) => {
    const errorKey = errorCode
    const data = {
      title: t(`errors.alerts.${errorKey}.title`),
      body: t(`errors.alerts.${errorKey}.body`),
      btnPrimary: t(`errors.alerts.${errorKey}.accept`),
      btnSecondary: t(`errors.alerts.${errorKey}.cancel`),
      headerImage: '/images/icons/mcd-bag.svg',
      verticalActions: true,
      onCloseUndefined: true,
    }
    // Remove error storage
    appStore.clearErrorCode()
    const result = await showDialogAlert({
      dialog,
      data,
    })
    return { result }
  }

  return {
    isComulativePromotionError,
    errorCumulativePromotion,
    showErrorPromotion,
  }
}
